import type { AmazonLoginResponse, TokenPayload } from '#root/api/clients/authentication/data-contracts'
import type { DialogPromise } from '#types/config/components/dialog'
import type { SignUpField } from '#types/config/components/form/signUp'
import { useApiCookie } from '#commerce/composables/useApiCookie'
import { getPkceCodePair } from '#core/utils/auth'
import { ApiErrorCode } from '#root/enums/api'
import { skipHydrate } from 'pinia'

/**
 * Authentication Store - manages user authentication, sessions, and login flows across all brands.
 *
 * **Key responsibilities:**
 * - User login/logout (regular, social, MFA, passkey)
 * - Guest/registered token management
 * - Employee authentication and discount T&C acceptance
 * - Post-auth onboarding dialogs (marketing enrollment, interests, profile enrichment)
 * - Session persistence and cookie management
 * - Multi-brand loyalty integration
 *
 * **Authentication methods supported:**
 * 1. **Regular login**: Email/password with optional captcha
 * 2. **Social login**: Google, Apple, Facebook OAuth
 * 3. **MFA/OTP**: One-time password verification
 * 4. **Passkey**: WebAuthn/FIDO2 passwordless login
 *
 * **Security considerations:**
 * - 🔒 Uses PKCE flow (Proof Key for Code Exchange) for token security
 * - 🔒 **Never log tokens, passwords, or PII**
 * - 🔒 **Employee discount T&C acceptance required** (stored in localStorage)
 * - 🔒 Refresh tokens stored in state, access tokens in cookies
 * - 🔒 Logout clears all tokens, cookies, and user data
 * - 🔒 Session cookies: `x-usid`, `vfa_{siteId}_login`, `vfa_{siteId}_access`
 *
 * **Multi-brand notes:**
 * - Store parameterized by countryCode for region isolation
 * - Employee login available for all brands
 * - Loyalty integration varies by brand (LaunchDarkly flags)
 * - Post-auth dialogs configurable per brand
 *
 * **Persisted data:**
 * - consumerNo (user ID)
 * - consumerType (EMPLOYEE | CONSUMER)
 * - lwaProfile (Amazon Login with Amazon profile)
 *
 * **Critical composable rules:**
 * - ⚠️ **Always call in component setup** (not inside methods/callbacks)
 * - ⚠️ **Cache store reference**, don't call useAuthStore() repeatedly
 * - ⚠️ **Never call inside onResponse hooks** (causes reactivity loss)
 *
 * @see domains/commerce/middleware/auth.ts for route protection
 * @see domains/commerce/components/form/login for UI integration
 * @see CONTRIBUTING.md#vuenuxt-composables---critical-rules for setup-only composable rules
 */

// Helper to reduce cognitive complexity
const isEmployeeDiscountTermsNotAccepted = (consumerType, isEmployeeDiscountTCAccepted) =>
  consumerType === 'EMPLOYEE' && !isEmployeeDiscountTCAccepted

export const useAuthStore = (countryCode = useCountryCode()) => defineStore(`auth_${countryCode}`, () => {
  const { $t, $feature } = useNuxtApp()
  const {
    DialogEmployeeTerms,
    DialogMarketingEnrollment,
    DialogInterests,
    DialogProfileEnrichment,
    DialogWelcome
  } = useDialogsStore()
  const { dialog, form } = useAppConfig().components
  const { isInterestsDependingOnNewsletter, toastCtaLoyaltyLink } = dialog.signUp
  const { fieldsOrder = [] } = form.signUp
  const { authentication, consumer } = useApi()
  const userStore = useUserStore()
  const cartStore = useCartStore()
  const subscriptions = useSubscriptions()
  const loyaltyStore = useLoyaltyStore()
  const route = useRoute()
  const favoritesStore = useFavoritesStore()
  const timelyDismissal = useTimelyDismissal()
  const toast = useToaster()

  // ─────────────────────────────────────────────────────────────────────────
  // State
  // ─────────────────────────────────────────────────────────────────────────

  /** User's unique consumer number (backend user ID) */
  const consumerNo = ref()

  /** Consumer type: 'EMPLOYEE' or 'CONSUMER' (affects discounts, features) */
  const consumerType = ref()

  /** Universal Session ID cookie (tracks guest and logged-in sessions) */
  const xUsidCookie = useCookie('x-usid')

  /** Logged-in status cookie (set by backend on successful auth) */
  const loggedInCookie = useApiCookie(cookiesPostfix.Login)

  /**
   * Computed logged-in status.
   * - Client: true if login cookie exists
   * - Server: undefined (prevents hydration mismatch)
   */
  const loggedIn = skipHydrate(computed(() => isClient() ? !!loggedInCookie.value : undefined))

  /** Pending state during initial auth check (undefined = still checking) */
  const pending = computed(() => loggedIn.value === undefined)

  /** In-store employee login status cookie (POS/associate login) */
  const inStoreEmployeeLoggedInCookie = useApiCookie(cookiesPostfix.Employee)

  /**
   * Is an in-store employee logged in? (not regular employee discount)
   * skipHydrate: client-only (prevents SSR mismatch)
   */
  const inStoreEmployeeLoggedIn = skipHydrate(
    computed(() => isClient() ? !!inStoreEmployeeLoggedInCookie.value : undefined)
  )

  /** Loyalty program member ID (populated after login if loyalty member) */
  const memberId = ref()

  /** 🔒 Refresh token (used to obtain new access tokens) - NEVER log this */
  const refreshToken = ref()

  /** 🔒 Access token (short-lived API authentication) - NEVER log this */
  const accessToken = ref<string | null>(null)

  /** Amazon Login with Amazon profile data (social login) */
  const lwaProfile = ref<AmazonLoginResponse | undefined>()

  /**
   * Employee discount Terms & Conditions acceptance flag.
   * - Stored in localStorage (persists across sessions)
   * - skipHydrate: prevents SSR mismatch
   * - Required before employees can use discount
   */
  const isEmployeeDiscountTCAccepted = skipHydrate(useLocalStorage('isEmployeeDiscountTCAccepted', false))

  /** Prevents showing loyalty enrollment toast multiple times per session */
  const isLoyaltyToastAlreadyDisplayed = useLocalStorage('isLoyaltyToastAlreadyDisplayed', false)

  // ─────────────────────────────────────────────────────────────────────────
  // Actions
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * Initialize guest access token if not already authenticated.
   *
   * **Called on app initialization** (plugins/auth.ts)
   *
   * **Behavior:**
   * - Skips if user is already logged in or has access token
   * - Requests guest token from backend
   * - Refreshes x-usid cookie
   * - Clears loyalty toast flag if user is not logged in
   *
   * **Error handling:**
   * - Ignores 403 errors (expected for some edge cases)
   * - Throws other API errors via assertApiError
   *
   * ⚠️ **Called automatically** - rarely needs manual invocation
   */
  const setAccess = async () => {
    // request a guest access token if it does not exist
    const accessToken = useApiCookie(cookiesPostfix.Access)

    // Clear toast flag if user is not logged in on site load
    if (!loggedIn.value) {
      isLoyaltyToastAlreadyDisplayed.value = false
    }

    if (loggedIn.value || accessToken.value) return

    try {
      await authentication.$token({
        type: 'Guest'
      })
    }
    catch (err: any) {
      log.error(`[@domains/commerce/stores/auth.ts::setAccess] Error type:<${err.status} ${err.statusText}>`)
      if (err.status !== 403)
        assertApiError(err)
    }

    refreshCookie('x-usid')
  }

  /**
   * Core login handler for all authentication methods.
   *
   * **Supports:**
   * - regular: Email/password login
   * - social: Google/Apple/Facebook OAuth
   * - MFA: One-time password verification
   * - passkey: WebAuthn/FIDO2 passwordless
   *
   * **Process:**
   * 1. Generate PKCE code pair (security)
   * 2. Obtain authorization code (via signIn or social/MFA/passkey flow)
   * 3. Exchange code for tokens
   * 4. Merge guest cart/favorites with logged-in user data
   * 5. Show employee T&C modal if needed
   * 6. Optionally redirect to /account (order linking)
   *
   * **Security:**
   * - 🔒 Uses PKCE flow to prevent auth code interception
   * - 🔒 Retries 409 conflicts (duplicate token edge case)
   * - 🔒 Clears guest data on successful login
   *
   * **Error handling:**
   * - Throws Error with translated message on failure
   * - Logs 403 errors (silently fails, no throw)
   *
   * @param {object} params - Login parameters
   * @param {'regular' | 'social' | 'MFA' | 'passkey'} params.type - Login method
   * @param {string} [params.username] - Email (regular login)
   * @param {string} [params.password] - Password (regular login)
   * @param {string} [params.token] - Auth token (social/MFA)
   * @param {'google' | 'apple' | 'facebook'} [params.provider] - OAuth provider
   * @param {string} [params.captchaResponse] - reCAPTCHA token
   * @param {object} [params.passkeyCredentials] - WebAuthn credentials
   * @param {object} params.options - Login options
   * @param {boolean} params.options.allowRedirect - Controls post-login redirect to /account
   * @param {boolean} params.options.postalCode - The customer's postal code that is required for registration on some markets
   *
   * @returns {Promise<object>} Token response with basketId, consumerNo, memberId, etc.
   *
   * ⚠️ **Internal use only** - call login(), socialLogin(), otpLogin(), or passkeyLogin() instead
   */
  const performLogin = async ({
    type,
    username,
    password,
    token,
    provider,
    captchaResponse,
    passkeyCredentials,
    options
  }: {
    type: 'regular' | 'social' | 'MFA' | 'OTPSignUp' | 'passkey'
    username?: string
    password?: string
    token?: string
    provider?: 'google' | 'apple' | 'facebook'
    captchaResponse?: string
    passkeyCredentials?: TokenPayload['passkeyCredentials']
    options?: { allowRedirect?: boolean, postalCode?: string }
  }) => {
    // capture data from the order to be provided when signing in
    const { order: { orderNumber = '', st = '' } } = useCheckoutStore()

    const { codeVerifier, codeChallenge } = await getPkceCodePair()

    let authorizationCode: string

    if (type !== 'regular') {
      authorizationCode = token!
    }
    else {
      const signInData = await authentication.$signIn({
        type: 'Registered',
        username: username!,
        password: password!,
        codeChallenge,
        recaptcha_response: captchaResponse!,
      })

      authorizationCode = signInData.authorizationCode
    }

    // register token
    let authTokenResponse

    const commonParams = {
      guestObjects: {
        basketId: cartStore.basketId || '',
        favoriteId: favoritesStore.favoriteId,
        saveForLaterId: cartStore.savedItems.saveForLaterId || ''
      },
      ...(orderNumber
        ? {
            orderNo: orderNumber,
            st,
            action: 'postLogin'
          }
        : {})
    }

    const retryConfig = {
      retry: 1,
      retryDelay: 2000,
      retryStatusCodes: [409]
    }

    try {
      // interim solution for GLOBAL15-62534
      if (type === 'social') {
        authTokenResponse = await authentication.$socialLogin({
          type: 'Social',
          idp: provider!,
          authorizationCode,
          ...commonParams
        }, undefined, retryConfig)
      }
      else if (type === 'MFA') {
        authTokenResponse = await authentication.$token({
          type: 'MFA',
          login: username,
          authorizationCode,
          ...commonParams
        }, retryConfig)
      }
      else if (type === 'OTPSignUp') {
        authTokenResponse = await authentication.$otpSignUp({
          type: 'MFA',
          email: username!,
          otpCode: authorizationCode,
          postalCode: options?.postalCode,
          subscriptions: {
            newsletterConsent: false,
            loyaltyConsent: true,
            smsNotificationConsent: false,
            loyaltyEnrollment: 'Full',
            loyaltyNewsletterConsent: true
          },
          source: {
            store: route.query.storeNum ? route.query.storeNum.toString() : useSiteId(),
            acquisitionType: 'Registered',
            campaignName: route.query.enrollment_marketing_source as string
          },
          ...commonParams,
        }, retryConfig)
      }
      else if (type === 'passkey') {
        authTokenResponse = await authentication.$token({
          type: 'Registered',
          codeVerifier,
          passkeyCredentials,
          authorizationCode,
          ...commonParams
        }, retryConfig)
      }
      else {
        authTokenResponse = await authentication.$token({
          type: 'Registered',
          codeVerifier,
          authorizationCode,
          ...commonParams
        }, retryConfig)
      }
      // redirect is not allowed while user links order from order-confirmation
      if (orderNumber && options?.allowRedirect) {
        // we need to wait until refreshApiCookie succeed
        // to pass through account auth guard
        watchOnce(loggedIn, () => {
          navigateTo('/account')
        })
      }
    }
    catch (err: any) {
      log.error(`[@domains/commerce/stores/auth.ts::login] Error type:<${err.status} ${err.statusText}>`)
      if (err.status !== 403) {
        throw new Error($t.apiMessages[ApiErrorCode.DUPLICATED_TOKEN], {
          cause: err
        })
      }
    }

    const {
      accessToken: userAccessToken,
      basketId: userBasketId,
      consumerNo: userConsumerNo,
      consumerType: userConsumerType,
      favorites,
      isEmployeeDiscountTCAccepted: isTCAccepted,
      memberId: userMemberId,
      refreshToken: userRefreshToken,
    } = authTokenResponse

    accessToken.value = userAccessToken

    isEmployeeDiscountTCAccepted.value = isTCAccepted

    if (userBasketId) {
      const previousTotalItems = cartStore.totalItems

      cartStore.basketId = userBasketId
      await cartStore.get({ mergeCarts: true, isPatchRequired: !!favoritesStore.favoriteId, keepNotifications: true })

      if (previousTotalItems && cartStore.totalItems > previousTotalItems) {
        cartStore.isCartMerged = true
      }
    }

    // this also triggers the favorites load, so favorites merge is obtained at sign in
    if (favorites) {
      favoritesStore.setFavoriteId(favorites.favoriteId)
      if (favorites.favStoreId) cartStore.favoriteStoreId = favorites.favStoreId
    }
    // saved for later list
    cartStore.fetchSavedCartItems()
    // Needed to determine whether athlete credit is available so we know whether loyalty rewards are allowed
    cartStore.getPaymentMethod()
    consumerNo.value = userConsumerNo
    consumerType.value = userConsumerType
    memberId.value = userMemberId
    refreshToken.value = userRefreshToken

    refreshCookie('x-usid')
    refreshCookie(`vfa_${useSiteId()}_${cookiesPostfix.Login}`)

    // Await openEmployeeTermsModal to resolve, prevent the modal be closed by page redirection
    if (isEmployeeDiscountTermsNotAccepted(userConsumerType, isTCAccepted))
      isEmployeeDiscountTCAccepted.value = await DialogEmployeeTerms.open()

    return authTokenResponse
  }

  /**
   * Login with One-Time Password (MFA).
   *
   * **Use case:** User enters OTP from email/SMS after initial login attempt.
   *
   * @param {string} username - User's email address
   * @param {string} authorizationCode - OTP authorization code from backend
   * @param {string} captchaResponse - reCAPTCHA token
   * @param {object} [options] - Login options
   * @param {boolean} [options.allowRedirect] - Controls post-login redirect to /account
   * @returns {Promise<object>} Token response
   *
   * @example
   * ```ts
   * const auth = useAuthStore()
   * const otpCode = '123456' // from user input
   * const captcha = await executeCaptcha()
   * await auth.otpLogin(email, otpCode, captcha)
   * ```
   */
  const otpLogin = async (
    username: string,
    authorizationCode: string,
    captchaResponse: string,
    options: { allowRedirect: boolean } = { allowRedirect: true }
  ) => await performLogin({
    type: 'MFA',
    username,
    token: authorizationCode,
    captchaResponse,
    options
  })

  /**
   * Sign up with One-Time Password (MFA).
   *
   * **Use case:** User enters OTP from email/SMS after initial login attempt and goes through passwordless registration.
   *
   * @param username - User's email address
   * @param authorizationCode - OTP authorization code from backend
   * @param captchaResponse - reCAPTCHA token
   * @param postalCode - The user's postal code, for markets where it is required
   * @returns Token response
   *
   * @example
   * ```ts
   * const auth = useAuthStore()
   * const otpCode = '123456' // from user input
   * const captcha = await executeCaptcha()
   * await auth.otpSignUp(email, otpCode, captcha)
   * ```
   */
  const otpSignUp = async (
    username: string,
    authorizationCode: string,
    captchaResponse: string,
    postalCode?: string
  ) => await performLogin({
    type: 'OTPSignUp',
    username,
    token: authorizationCode,
    captchaResponse,
    options: {
      postalCode
    }
  })

  /**
   * Login with email and password.
   *
   * **Standard authentication method** for registered users.
   *
   * **Security:**
   * - 🔒 Password never logged or stored client-side
   * - 🔒 Requires reCAPTCHA to prevent brute force
   * - 🔒 Uses PKCE flow for token security
   *
   * @param {string} username - User's email address
   * @param {string} password - User's password (NEVER log this)
   * @param {string} captchaResponse - reCAPTCHA v3 token
   * @param {object} [options] - Login options
   * @param {boolean} [options.allowRedirect] - Controls post-login redirect to /account
   * @returns {Promise<object>} Token response
   *
   * @example
   * ```ts
   * const auth = useAuthStore() // Call ONCE in setup
   * const captcha = await executeCaptcha('login')
   * await auth.login(email, password, captcha)
   * // User is now logged in, cart/favorites merged
   * ```
   */
  const login = async (
    username: string,
    password: string,
    captchaResponse: string,
    options: { allowRedirect: boolean } = { allowRedirect: true }
  ) => await performLogin({
    type: 'regular',
    username,
    password,
    captchaResponse,
    options
  })

  /**
   * Login with social OAuth provider (Google, Apple, Facebook).
   *
   * **Process:**
   * 1. User completes OAuth flow (handled by third-party SDK)
   * 2. Frontend receives token from provider
   * 3. Backend verifies token and creates/links account
   * 4. Returns session tokens
   *
   * @param {string} token - OAuth token from provider
   * @param {'google' | 'apple' | 'facebook'} provider - OAuth provider
   * @param {object} [options] - Login options
   * @param {boolean} [options.allowRedirect] - Controls post-login redirect to /account
   * @returns {Promise<object>} Token response
   *
   * @example
   * ```ts
   * const auth = useAuthStore()
   * const googleToken = await googleAuth.signIn()
   * await auth.socialLogin(googleToken, 'google')
   * ```
   */
  const socialLogin = async (
    token: string,
    provider: 'google' | 'apple' | 'facebook',
    options: { allowRedirect: boolean } = { allowRedirect: true }
  ) => await performLogin({
    type: 'social',
    token,
    provider,
    options
  })

  /**
   * Login with WebAuthn/FIDO2 passkey (passwordless authentication).
   *
   * **Modern authentication** using biometric/hardware keys.
   *
   * @param {object} passkeyCredentials - WebAuthn credentials from browser API
   * @param {object} [options] - Login options
   * @param {boolean} [options.allowRedirect] - Controls post-login redirect to /account
   * @returns {Promise<object>} Token response
   *
   * @example
   * ```ts
   * const auth = useAuthStore()
   * const credentials = await navigator.credentials.get({ publicKey: {...} })
   * await auth.passkeyLogin(credentials)
   * ```
   */
  const passkeyLogin = async (
    passkeyCredentials: TokenPayload['passkeyCredentials'],
    options: { allowRedirect: boolean } = { allowRedirect: true }
  ) => await performLogin({
    type: 'passkey',
    passkeyCredentials,
    options
  })

  /**
   * Logout current user and clear all session data.
   *
   * **Process:**
   * 1. Redirect to home if on protected route (cart, checkout, favorites, account)
   * 2. Call backend signOut API
   * 3. Clear all cookies (login, employee, x-usid)
   * 4. Clear all store state (user, cart, loyalty, favorites)
   * 5. Request new guest token
   * 6. Show success toast
   *
   * **Security:**
   * - 🔒 Clears all tokens and cookies
   * - 🔒 Resets employee discount T&C flag
   * - 🔒 Prevents stale session access
   *
   * **Side effects:**
   * - Navigates away from protected routes
   * - Resets cart, favorites, loyalty stores
   * - Clears loyalty toast flag
   *
   * @example
   * ```ts
   * const auth = useAuthStore()
   * await auth.logout()
   * // User is now logged out, redirected to home if needed
   * ```
   */
  const logout = async () => {
    const router = useRouter()
    const toast = useToaster()

    // We need to redirect to home page if we're on these routes
    const routesToRedirectFrom = ['cart', 'checkout', 'favorites', 'account']
    if (route.path?.split('/').some((p) => routesToRedirectFrom.includes(p))) {
      await router.push(useLocalisedRoute('/'))
    }

    try {
      await authentication.$signOut({
        refreshToken: refreshToken.value
      })
    }
    catch (err: any) {
      // Log but don't block logout — SLAS may reject the token (e.g. already expired,
      // invalid organizationId AUT437) but we still need to clear the local session.
      log.error(`[@domains/commerce/stores/auth.ts::logout] $signOut error: ${err?.status} ${err?.data?.defaultError || err?.message}`)
    }

    // clear token cookie
    loggedInCookie.value = null
    inStoreEmployeeLoggedInCookie.value = null
    xUsidCookie.value = null

    // clear user data
    accessToken.value = null
    consumerNo.value = null
    consumerType.value = null
    memberId.value = null
    refreshToken.value = null
    isEmployeeDiscountTCAccepted.value = false

    // Clear toast flag
    isLoyaltyToastAlreadyDisplayed.value = false

    // clear user
    userStore.reset()

    // clear cart
    cartStore.reset()

    // clear loyalty
    loyaltyStore.reset()

    // clear the favorites
    favoritesStore.setFavoriteId(null)
    favoritesStore.setFavoritesCount(0)

    // request new guest token
    try {
      await authentication.$token({
        type: 'Guest'
      })
    }
    catch (err: any) {
      log.error(`[@domains/commerce/stores/auth.ts::logout] Error type:<${err.status} ${err.statusText}>`)
      if (err.status !== 403)
        assertApiError(err)
    }

    toast.add({
      props: {
        title: 'Success',
        message: $t.signOutSuccess,
        type: 'success'
      }
    })
  }

  /**
   * Get loyalty member ID from backend.
   *
   * **Behavior:**
   * - Only called if: loyalty features enabled AND user is loyalty member AND memberId not cached
   * - Returns cached memberId if already loaded
   * - Returns undefined if not a loyalty member
   *
   * **Use case:** Required for loyalty rewards, points balance, tier status.
   *
   * @returns {Promise<string | undefined>} Loyalty member ID
   *
   * @example
   * ```ts
   * const auth = useAuthStore()
   * const memberId = await auth.getMemberId()
   * if (memberId) {
   *   // User is loyalty member, fetch rewards
   * }
   * ```
   */
  const getMemberId = async () => {
    if ($feature.enableLoyaltyFeatures && userStore.isLoyaltyMember && !memberId.value) {
      const loyaltyResponse = await consumer.$getLoyaltyToken({
        usid: xUsidCookie.value || uuid()
      }, {
        onRequest: (context) => {
          if (route.query.leadSource) {
            context.options.headers = {
              ...context.options.headers,
              source: route.query.leadSource
            } as Headers
          }
        }
      })
      memberId.value = loyaltyResponse.memberId
    }

    return memberId.value
  }

  const postLoginActions = async () => {
    if (!userStore.profile) await userStore.get()
    await nextTick()

    const campaignName = route.query.enrollment_marketing_source as string
    const details = userStore.profile?.consumerDetails as any

    if (
      campaignName
      && details?.source?.campaignName !== campaignName
      && details?.lastSourceUpdate?.campaignName !== campaignName
    ) {
      userStore.updateBasicInfo({ source: { campaignName } }).catch(() => {})
    }
  }

  /**
   * Show post-authentication onboarding dialogs.
   *
   * **Dialogs shown (conditional, in order):**
   * 1. **Marketing Enrollment**: SMS/email opt-in (if not already subscribed)
   * 2. **Profile Enrichment**: Collect missing profile data (name, birthday, zip)
   * 3. **Interests**: Collect user interests/preferences
   * 4. **Welcome**: Final welcome message (only if at least one prior dialog shown)
   *
   * **Feature flags control:**
   * - marketingEnrollmentOnSignUp (register flow)
   * - tempEnablePostAuthOnboarding (login flow)
   * - collectInterestsAtSignUp (register flow)
   * - showWelcomeModalAfterInterestsAtSignUp (register flow)
   *
   * **Timely dismissal:**
   * - Each dialog has dismissal tracking (prevent showing again for X days)
   * - Keyed by consumerNo to track per-user
   *
   * **Behavior:**
   * - Waits for user profile to load
   * - Opens dialogs sequentially (not parallel)
   * - Aborts remaining dialogs if user logs out mid-flow
   * - Shows loyalty toast after registration (if dialogs shown)
   *
   * @param {'login' | 'register'} context - Triggered from login or registration
   *
   * @example
   * ```ts
   * const auth = useAuthStore()
   * await auth.login(email, password, captcha)
   * await auth.showPostAuthDialogs('login')
   * ```
   */
  const showPostAuthDialogs = async (context: 'login' | 'register') => {
    if (!userStore.profile) await userStore.get() // ensure profile is loaded before evaluating which dialogs to show
    await nextTick()

    const dialogsQueue = [] as DialogPromise<undefined, any, unknown>[]

    if (
      ((
        context === 'register'
        && $feature.marketingEnrollmentOnSignUp
      ) || (
        context === 'login'
        && $feature.tempEnablePostAuthOnboarding
      ))
      && timelyDismissal.check(`marketing-enrollment_${consumerNo.value}`)
      && (!subscriptions.smsSubscription.value?.optin || !subscriptions.newsletterSubscribed.value)
    ) {
      dialogsQueue.push(DialogMarketingEnrollment)
    }

    if (
      $feature.tempEnablePostAuthOnboarding
      && timelyDismissal.check(`profile-enrichment_${consumerNo.value}`)
      && (
        !userStore.profile?.consumerDetails.firstName
        || !userStore.profile?.consumerDetails.lastName
        || !userStore.profile?.consumerDetails.birthDate
        || ((fieldsOrder as SignUpField[]).includes('zipCode') && !userStore.profile?.consumerDetails.postalCode)
      )
    ) {
      dialogsQueue.push(DialogProfileEnrichment)
    }

    if (
      ((
        context === 'register'
        && $feature.collectInterestsAtSignUp
        && (!isInterestsDependingOnNewsletter || subscriptions.newsletterSubscribed.value)
      ) || (
        context === 'login'
        && $feature.tempEnablePostAuthOnboarding
      ))
      && timelyDismissal.check(`interests_${consumerNo.value}`)
      && !userStore.interests.length
    ) {
      dialogsQueue.push(DialogInterests)
    }

    // push the welcome dialog only if at least one of the preceeding dialogs is shown
    if ((
      context === 'register'
      && $feature.showWelcomeModalAfterInterestsAtSignUp
      && dialogsQueue.length > 0
    ) || (
      context === 'login'
      && $feature.tempEnablePostAuthOnboarding
      && dialogsQueue.length > 0
    )) {
      dialogsQueue.push(DialogWelcome)
    }

    // open dialogs sequentially
    for (const Dialog of dialogsQueue) {
      if (!loggedIn.value) return // abort displaying rest of the dialogs if user has logged out
      await Dialog.open()
    }

    // finally show toast with CTA for newly registered users
    if (context === 'register') {
      toast.add({
        props: {
          title: $t.accountCreated,
          message: $t.completeProfile,
          type: 'success',
          ctas: [{
            label: $t.goToMyAccount,
            to: $feature.enableLoyaltyFeatures && toastCtaLoyaltyLink?.requiredLocales.includes(useLocale())
              ? '/account/loyalty'
              : '/account',
          }]
        },
        delay: 5000
      })
    }
  }

  // ─────────────────────────────────────────────────────────────────────────
  // Return (Public API)
  // ─────────────────────────────────────────────────────────────────────────

  return {
    /** Computed: Current consumer ID (logged-in cookie OR x-usid for guests) */
    consumerId: computed(() => loggedInCookie.value || xUsidCookie.value),

    /** User's unique consumer number (backend user ID) */
    consumerNo,

    /** Consumer type: 'EMPLOYEE' or 'CONSUMER' */
    consumerType,

    /** Get loyalty member ID (async, cached after first call) */
    getMemberId,

    /** Is in-store employee logged in? (POS/associate login) */
    inStoreEmployeeLoggedIn,

    /** Computed: Is user an employee? (consumerType === 'EMPLOYEE') */
    isEmployee: computed(() => consumerType.value === 'EMPLOYEE'),

    /** Has employee accepted discount Terms & Conditions? */
    isEmployeeDiscountTCAccepted,

    /** Computed: Is user logged in? (undefined = pending) */
    loggedIn,

    /** Login with email/password */
    login,

    /** Logout and clear session */
    logout,

    /** Amazon Login with Amazon profile */
    lwaProfile,

    /** Loyalty member ID (if loyalty member) */
    memberId,

    /** Computed: Is auth state pending? (true if loggedIn === undefined) */
    pending,

    /** Run follow-up actions after login */
    postLoginActions,

    /** Initialize guest access token */
    setAccess,

    /** Show post-auth onboarding dialogs */
    showPostAuthDialogs,

    /** Login with social OAuth (Google/Apple/Facebook) */
    socialLogin,

    /** Login with OTP/MFA */
    otpLogin,

    /** Sign up with OTP/MFA */
    otpSignUp,

    /** Login with passkey (WebAuthn) */
    passkeyLogin,

    /** 🔒 Access token (NEVER log this) */
    accessToken
  }
}, {
  persist: {
    pick: ['consumerNo', 'consumerType', 'lwaProfile']
  }
})()
